import { IElement } from "core-integration/src/store/elements/types";
import { ITeam } from "core-integration/src/store/teams/types";
import { integerToMoney } from "core-integration/src/utils/money";
import { rgba, size } from "polished";
import * as React from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import MinusIcon from "../img/icons/minus.svg?react";
import PlusIcon from "../img/icons/plus-element.svg?react";
import { getElementStatValue } from "../utils/stats";
import ElementDialogButton from "./ElementDialogButton";
import ElementInTable from "./ElementInTable";
import { ElementCell, ElementRow } from "./ElementTable";

const MobileActionButton = styled.button`
  display: block;
  width: 24px;
  height: 24px;
  margin: auto;
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const IconStyles = css`
  ${size(18)};
  display: block;
  margin: auto;
  fill: currentColor;
`;

const AddPlayerIcon = styled(PlusIcon)<{ $canAddElement: boolean }>`
  ${IconStyles};
  color: ${({ theme }) => theme.colors.green};
  opacity: ${(props) => (props.$canAddElement ? "1" : "0.4")};
  cursor: ${(props) => (props.$canAddElement ? "pointer" : "not-allowed")};
`;

const RemovePlayerIcon = styled(MinusIcon)`
  ${IconStyles};
  color: ${({ theme }) => theme.colors.red};
`;

const MobileActionCell = styled.td`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const StyledElementListRow = styled(ElementRow)<IStyledProps>`
  ${(props) =>
    props.isProposed &&
    css`
      td,
      button {
        color: ${rgba("black", 0.6)};
      }

      img {
        opacity: 0.6;
      }
    `}
  > td:last-child {
    word-break: break-all;
    white-space: normal;
  }
`;

const ElementListStatCell = styled.td`
  font-family: ${(props) => props.theme.fonts.body};
  font-size: 2rem;
  &&& {
    font-weight: 500;
  }
`;

interface IStyledProps {
  isProposed: boolean;
}

interface IProps extends IStyledProps {
  currencyDivisor: number;
  element: IElement;
  handleAddElement: () => void;
  handleRemoveElement: (position: number) => void;
  hasRoomInPosition: boolean;
  proposedPosition: number;
  renderElementDialog: () => void;
  renderElementMenu: () => void;
  sort: string | number;
  team: ITeam;
}

const ElementListRow = ({
  currencyDivisor,
  element,
  hasRoomInPosition,
  isProposed,
  proposedPosition,
  handleAddElement,
  handleRemoveElement,
  renderElementMenu,
  sort,
}: IProps) => {
  const { t } = useTranslation();
  const canAddElement = hasRoomInPosition;
  return (
    <StyledElementListRow isProposed={isProposed}>
      <td>
        <ElementDialogButton elementId={element.id} variant="list" />
      </td>
      <ElementCell>
        <ElementInTable
          renderElementMenu={renderElementMenu}
          elementId={element.id}
        />
      </ElementCell>
      <ElementListStatCell>
        {integerToMoney(element.now_cost, currencyDivisor)}
      </ElementListStatCell>
      <ElementListStatCell>
        {getElementStatValue(element, sort as keyof IElement, currencyDivisor)}
      </ElementListStatCell>
      {isProposed ? (
        <MobileActionCell>
          <MobileActionButton
            aria-label={t("elementList.removePlayer", "Remove player")}
            onClick={() => handleRemoveElement(proposedPosition)}
          >
            <RemovePlayerIcon aria-hidden="true" focusable="false" />
          </MobileActionButton>
        </MobileActionCell>
      ) : (
        <MobileActionCell>
          <MobileActionButton
            aria-label={t("elementList.addPlayer", "Add player")}
            disabled={!canAddElement}
            onClick={() => handleAddElement()}
          >
            <AddPlayerIcon
              $canAddElement={canAddElement}
              aria-hidden="true"
              focusable="false"
            />
          </MobileActionButton>
        </MobileActionCell>
      )}
    </StyledElementListRow>
  );
};

export default ElementListRow;
